import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["super_admin", "direction", "administration", "finance", "coordinateur", "professeur", "parent", "eleve"];

export default function Users() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState({ students: [], parents: [], teachers: [], families: [] });
  const empty = { email: "", password: "", name: "", roles: [], phone: "", student_id: "", parent_id: "", teacher_id: "", family_id: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/users"); setRows(data);
    const [s, p, te, f] = await Promise.all([
      api.get("/students"), api.get("/parents"), api.get("/teachers"), api.get("/families"),
    ]);
    setLinks({ students: s.data, parents: p.data, teachers: te.data, families: f.data });
  };
  useEffect(() => { load(); }, []);

  const toggleRole = (r) => setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));

  const submit = async () => {
    try {
      const payload = { ...form };
      ["student_id", "parent_id", "teacher_id", "family_id"].forEach((k) => { if (!payload[k]) delete payload[k]; });
      await api.post("/users", payload);
      toast.success(t("save")); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const remove = async (id) => { if (!window.confirm(`${t("delete")} ?`)) return; await api.delete(`/users/${id}`); load(); };

  const setStatus = async (id, status) => {
    if ((status === "suspended" || status === "archived") &&
        !window.confirm("Cette action peut bloquer l'accès de l'utilisateur à InZeAir. Confirmer ?")) return;
    try { await api.put(`/users/${id}/status`, { status }); toast.success(t("save")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const STCOLOR = { active: "bg-emerald-100 text-emerald-700", suspended: "bg-red-100 text-red-700", archived: "bg-slate-100 text-slate-500", pending: "bg-amber-100 text-amber-700" };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("users")}</h1>
        <div className="flex items-center gap-3">
          <a href="/audit?module=admin" data-testid="admin-history-link" className="text-sm text-primary hover:underline">{t("viewHistory")}</a>
          <Button data-testid="user-add-btn" onClick={() => { setForm(empty); setOpen(true); }} className="rounded-full gap-2"><Plus className="h-4 w-4" />{t("add")}</Button>
        </div>
      </div>
      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
              <TableHead>{t("name")}</TableHead><TableHead>{t("email")}</TableHead><TableHead>{t("roles")}</TableHead><TableHead>{t("status")}</TableHead><TableHead className="text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u, i) => (
              <TableRow key={u.id} data-testid="row-users" className={i % 2 ? "bg-slate-50/40" : ""}>
                <TableCell className="font-medium text-slate-800">{u.name}</TableCell>
                <TableCell className="text-slate-600">{u.email}</TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{(u.roles || []).map((r) => <Badge key={r} variant="secondary" className="text-[10px]">{t(`role_${r}`)}</Badge>)}</div></TableCell>
                <TableCell><Badge className={`${STCOLOR[u.status || "active"]} border-0`}>{t(`ust_${u.status || "active"}`)}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {(u.status === "suspended" || u.status === "archived") ?
                      <Button data-testid="user-activate" variant="ghost" size="sm" onClick={() => setStatus(u.id, "active")}>{t("reactivate")}</Button> :
                      <Button data-testid="user-suspend" variant="ghost" size="sm" onClick={() => setStatus(u.id, "suspended")}>{t("suspend")}</Button>}
                    <Button data-testid="user-archive" variant="ghost" size="sm" onClick={() => setStatus(u.id, "archived")}>{t("archive")}</Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(u.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{t("add")} — {t("users")}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2 max-h-[62vh] overflow-y-auto pr-1">
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("name")}</label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("email")}</label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("password")}</label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("phone")}</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="grid gap-2">
              <label className="text-sm font-medium text-slate-600">{t("roles")}</label>
              <div className="grid grid-cols-2 gap-2">
                {ROLES.map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm text-slate-700">
                    <Checkbox data-testid={`role-${r}`} checked={form.roles.includes(r)} onCheckedChange={() => toggleRole(r)} />
                    {t(`role_${r}`)}
                  </label>
                ))}
              </div>
            </div>
            {form.roles.includes("eleve") && <LinkSelect label={t("student")} items={links.students} value={form.student_id} onChange={(v) => setForm({ ...form, student_id: v })} />}
            {form.roles.includes("parent") && <LinkSelect label={t("parents")} items={links.parents} value={form.parent_id} onChange={(v) => setForm({ ...form, parent_id: v })} />}
            {form.roles.includes("professeur") && <LinkSelect label={t("teacher")} items={links.teachers} value={form.teacher_id} onChange={(v) => setForm({ ...form, teacher_id: v })} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="user-save-btn" onClick={submit}>{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LinkSelect({ label, items, value, onChange }) {
  return (
    <div className="grid gap-1.5">
      <label className="text-sm font-medium text-slate-600">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>{items.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
